import type { CSSProperties } from "react";

export const COGNITORY_ACTIVITY_EVALUATION_RATINGS = [
  "acceptable",
  "marginal",
  "unacceptable",
] as const;

/** Vivid semantic palette for evaluation ratings (dots, accents, selected borders). */
export const COGNITORY_ACTIVITY_EVALUATION_SEMANTIC_COLORS = {
  acceptable: {
    vivid: "#16A34A",
    selectedBackground: "rgba(22, 163, 74, 0.12)",
    selectedBorder: "#16A34A",
  },
  marginal: {
    vivid: "#F59E0B",
    selectedBackground: "rgba(245, 158, 11, 0.14)",
    selectedBorder: "#F59E0B",
  },
  unacceptable: {
    vivid: "#DC2626",
    selectedBackground: "rgba(220, 38, 38, 0.1)",
    selectedBorder: "#DC2626",
  },
  not_evaluated: {
    vivid: "#94a3b8",
  },
} as const;

export type CognitoryActivityEvaluationRating =
  (typeof COGNITORY_ACTIVITY_EVALUATION_RATINGS)[number];

export type CognitoryActivityEvaluationStatus =
  | "not_evaluated"
  | CognitoryActivityEvaluationRating;

export type CognitoryActivityUserEvaluationView = {
  rating: CognitoryActivityEvaluationRating;
  updatedAt: string;
};

const RATING_SET = new Set<string>(COGNITORY_ACTIVITY_EVALUATION_RATINGS);

export function deriveCognitoryActivityEvaluationStatus(
  userEvaluation: CognitoryActivityUserEvaluationView | null,
): CognitoryActivityEvaluationStatus {
  if (!userEvaluation) {
    return "not_evaluated";
  }
  return userEvaluation.rating;
}

export function formatCognitoryActivityEvaluationStatusLabel(
  status: CognitoryActivityEvaluationStatus,
): string {
  switch (status) {
    case "not_evaluated":
      return "Not evaluated";
    case "acceptable":
      return "Acceptable";
    case "marginal":
      return "Marginal";
    case "unacceptable":
      return "Unacceptable";
    default:
      return "Not evaluated";
  }
}

export function cognitoryActivityEvaluationStatusDotColor(
  status: CognitoryActivityEvaluationStatus,
): string {
  switch (status) {
    case "acceptable":
      return COGNITORY_ACTIVITY_EVALUATION_SEMANTIC_COLORS.acceptable.vivid;
    case "marginal":
      return COGNITORY_ACTIVITY_EVALUATION_SEMANTIC_COLORS.marginal.vivid;
    case "unacceptable":
      return COGNITORY_ACTIVITY_EVALUATION_SEMANTIC_COLORS.unacceptable.vivid;
    default:
      return COGNITORY_ACTIVITY_EVALUATION_SEMANTIC_COLORS.not_evaluated.vivid;
  }
}

export function cognitoryActivityEvaluationRatingChoiceStyle(input: {
  rating: CognitoryActivityEvaluationRating;
  selected: boolean;
}): CSSProperties {
  const semantic = COGNITORY_ACTIVITY_EVALUATION_SEMANTIC_COLORS[input.rating];
  if (input.selected) {
    return {
      border: `2px solid ${semantic.selectedBorder}`,
      background: semantic.selectedBackground,
      color: "#1e293b",
    };
  }
  return {
    border: "1px solid rgba(148, 163, 184, 0.55)",
    background: "rgba(255, 255, 255, 0.72)",
    color: "#334155",
  };
}

export function cognitoryActivityEvaluationListRowAccentStyle(
  status: CognitoryActivityEvaluationStatus,
): CSSProperties {
  if (status === "not_evaluated") {
    return {};
  }
  const color = cognitoryActivityEvaluationStatusDotColor(status);
  return {
    borderLeft: `4px solid ${color}`,
  };
}

/** Row frame borders without `border` shorthand so left accent is not reset. */
export function cognitoryActivityEvaluationListRowFrameStyle(input: {
  status: CognitoryActivityEvaluationStatus;
  panelBorderColor: string;
}): CSSProperties {
  const edge = `1px solid ${input.panelBorderColor}`;
  const accent = cognitoryActivityEvaluationListRowAccentStyle(input.status);
  if (!accent.borderLeft) {
    return { border: edge };
  }
  return {
    borderTop: edge,
    borderRight: edge,
    borderBottom: edge,
    borderLeft: accent.borderLeft,
  };
}

export function parseCognitoryActivityUserEvaluation(
  value: unknown,
): CognitoryActivityUserEvaluationView | null {
  if (value === null) {
    return null;
  }
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  const rating = String(record.rating ?? "");
  const updatedAt = String(record.updatedAt ?? "");
  if (!RATING_SET.has(rating) || updatedAt.length === 0) {
    return null;
  }
  return {
    rating: rating as CognitoryActivityEvaluationRating,
    updatedAt,
  };
}
